import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/components/common/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const RESTART = /Sign in again/;

export function CodeStep({ challenge, onBack }) {
  const { verifyCode } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [stay, setStay] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(challenge.resend_in);
  const [masked, setMasked] = useState(challenge.masked_email);
  const restart = RESTART.test(error);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code");
    setBusy(true);
    try {
      const user = await verifyCode(challenge.ticket, code, stay);
      navigate(user.role === "admin" ? "/dashboard" : "/add", { replace: true });
    } catch (err) { setError(errMsg(err)); setCode(""); }
    setBusy(false);
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend", { ticket: challenge.ticket });
      setWait(data.resend_in); setMasked(data.masked_email); setError(""); setCode("");
      toast.success("New code sent");
    } catch (err) {
      const m = errMsg(err);
      const left = m.match(/^Resend in (\d+)s$/);
      if (left) setWait(Number(left[1])); else setError(m);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" noValidate data-testid="code-form">
      <div>
        <h2 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-7 w-7 text-sky-600" />Check your email</h2>
        <p className="text-sm text-muted-foreground mt-2 break-words" data-testid="code-sent-to">Enter the 6-digit code we sent to {masked}</p>
      </div>
      <div>
        <Label htmlFor="code" className="eyebrow">Sign-in code</Label>
        <Input id="code" name="one-time-code" value={code} inputMode="numeric" autoComplete="one-time-code" autoFocus
          onPaste={(e) => { e.preventDefault(); setCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6)); setError(""); }}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
          className="mt-2 h-14 rounded-xl text-center text-3xl font-num tracking-[0.5em] pl-[0.5em]" placeholder="••••••" disabled={restart} data-testid="code-input" />
      </div>
      <label className="flex items-start gap-3 text-sm cursor-pointer" htmlFor="stay">
        <Checkbox id="stay" checked={stay} onCheckedChange={(v) => setStay(v === true)} className="mt-0.5 h-5 w-5" data-testid="stay-logged-in-checkbox" />
        Stay logged in on this device for 30 days
      </label>
      <FormError message={error} testId="code-error" />
      <p className="text-xs text-muted-foreground" data-testid="code-spam-tip">No code after a minute? Check your spam or junk folder, then tap Resend code.</p>
      {restart ? (
        <Button type="button" onClick={onBack} className="w-full h-12 rounded-xl text-base font-semibold" data-testid="code-restart-button">Sign in again</Button>
      ) : (
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-xl text-base font-semibold" data-testid="code-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Verify and sign in
        </Button>
      )}
      <div className="flex items-center justify-between gap-3 text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground" data-testid="code-back-button">Use a different account</button>
        {!restart && (
          <button type="button" onClick={resend} disabled={wait > 0} className="font-semibold text-sky-700 disabled:text-muted-foreground" data-testid="code-resend-button">
            {wait > 0 ? `Resend in ${wait}s` : "Resend code"}
          </button>
        )}
      </div>
    </form>
  );
}
